import { ActionReducerMapBuilder, createSlice } from '@reduxjs/toolkit';
import { CreateUserOfflineAddressPayload, UpdateUserOfflineAddressPayload } from '../../models/payload/address';
import { OfflineCustomerAddressResponse, offlineAddress } from '../../models/response/address';
import {
  createCustomerOfflineAddress,
  getCustomerOfflineAddress,
  removeCustomerAddress,
  updateCustomerOfflineAddress,
} from '../../services/address';
import { createAppAsyncThunk } from '../../utils/reduxUtil';
import offlineOrderingSlice from '../offlineOrdering';

type AddressState = {
  addresses: Array<offlineAddress>;
  openAddForm: boolean;
  loading: boolean;
};

const initialState: AddressState = {
  addresses: [],
  openAddForm: false,
  loading: false,
};

export const getOfflineAddress = createAppAsyncThunk<
  OfflineCustomerAddressResponse,
  { id: string; networkStatus: boolean }
>('address/getCustomerOfflineAddress', async (command) => {
  const response = await getCustomerOfflineAddress(command?.id, command?.networkStatus);
  return response;
});

export const createOfflineAddress = createAppAsyncThunk<
  void,
  CreateUserOfflineAddressPayload & { networkStatus: boolean }
>('address/addOfflineAddress', async (command, { dispatch }) => {
  const response = await createCustomerOfflineAddress(command, command?.networkStatus);
  const { id } = (response as unknown as { data: { id: string } }).data;
  await dispatch(getOfflineAddress({ id: command.userId || '', networkStatus: command?.networkStatus }));
  await dispatch(offlineOrderingSlice.actions.addSelectedAddress(id));
  return response;
});

export const updateOfflineAddress = createAppAsyncThunk<
  void,
  UpdateUserOfflineAddressPayload & { customerId: string; networkStatus: boolean }
>('address/updateOfflineAddress', async (command, { dispatch }) => {
  const response = await updateCustomerOfflineAddress(command);
  await dispatch(getOfflineAddress({ id: command.customerId || '', networkStatus: command?.networkStatus }));
  return response;
});

export const removeOfflineAddress = createAppAsyncThunk<
  void,
  { customerId: string; addressId: string; networkStatus: boolean }
>('address/removeOfflineAddress', async ({ addressId, customerId, networkStatus }, { dispatch }) => {
  const response = await removeCustomerAddress(addressId);
  await dispatch(getOfflineAddress({ id: customerId || '', networkStatus }));
  return response;
});

const offlineAddressSlice = createSlice({
  name: 'offlineOrdering',
  initialState,
  reducers: {
    toggleAddFrom: (state, action) => ({ ...state, openAddForm: action.payload }),
    setOfflineAddress: (state, action) => ({ ...state, addresses: action.payload }),
  },
  extraReducers: (builder: ActionReducerMapBuilder<AddressState>) => {
    builder.addCase(getOfflineAddress.pending, (state) => ({ ...state, loading: true }));
    builder.addCase(getOfflineAddress.fulfilled, (state, action) => ({
      ...state,
      loading: false,
      addresses: action.payload?.data || [],
      openAddForm: false,
    }));
    builder.addCase(createOfflineAddress.pending, (state) => ({ ...state, loading: true }));
    builder.addCase(createOfflineAddress.fulfilled, (state) => ({
      ...state,
      loading: false,
    }));
    builder.addCase(createOfflineAddress.rejected, (state) => ({
      ...state,
      loading: false,
    }));

    builder.addCase(removeOfflineAddress.pending, (state) => ({ ...state, loading: true }));
    builder.addCase(removeOfflineAddress.fulfilled, (state) => ({
      ...state,
      loading: false,
    }));
    builder.addCase(removeOfflineAddress.rejected, (state) => ({
      ...state,
      loading: false,
    }));

    builder.addCase(updateOfflineAddress.pending, (state) => ({ ...state, loading: true }));
    builder.addCase(updateOfflineAddress.fulfilled, (state) => ({
      ...state,
      loading: false,
    }));
    builder.addCase(updateOfflineAddress.rejected, (state) => ({
      ...state,
      loading: false,
    }));
  },
});

export default offlineAddressSlice;
